// ─────────────────────────────────────────────
// ENUMS (from backend consts)
// ─────────────────────────────────────────────

import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import {
  draftingStatusConst,
  draftingTranscriptionStatusConst,
} from "../constants";
import { IBrief } from "./brief";
import {
  BriefCampaignInf,
  ICampaignDeliverable,
  ICampaignDeliverableTask,
} from "./briefCampains";
import { IGetBriefConfigResponse } from "./briefConfig";
import { ICreatedBy } from "./briefInfluencerV2";

export type UserType = string;
// If you want exact union, replace with:
// export type UserType = keyof typeof userConst.type;

// export type ContentUrlType = string;
// Same here; replace with keys if available:
// export type ContentUrlType = keyof typeof campaignConst.deliverable.task.form.contentUrlType;

// ─────────────────────────────────────────────
// BASE TYPES
// ─────────────────────────────────────────────

export enum ComplianceCheckStatusReportEnum {
  NonCompliant = "Non-Compliant",
  Compliant = "Compliant",
  PartiallyCompliant = "Partially Compliant",
}

export interface IComplianceChecksElementary {
  point: string;
  status: ComplianceCheckStatusReportEnum; // Compliant, Non-Compliant
  start?: number;
  reason?: string;
}

export interface IComplianceReport {
  score: number;
  status: string; // Non-Compliant, Compliant
  brandGuardRailsChecks?: IComplianceChecksElementary[];
  advancedPromptingChecks?: IComplianceChecksElementary[];
  performanceGuidelinesChecks?: IComplianceChecksElementary[];
  consistencyChecks?: IComplianceChecksElementary[];
  safetyChecks?: IComplianceChecksElementary[];
  isAiBrandGuardRailsChecks?: boolean;
  isAiAdvancedPromptingChecks?: boolean;
  isAiPerformanceChecks?: boolean;
  isAiConsistencyCheck?: boolean;
  isAiSafetyChecks?: boolean;
  isAiScriptVsDraftingConsistencyCheck?: boolean;
  scriptVsDraftingConsistencyChecks?: IComplianceChecksElementary[];
}

export interface IDraftingMediaAssets {
  _id: string;
  type: BriefDeliverableTaskUploadTypeEnum;
  url: string;
  name: string;
  description?: string;
  aiWorkspaceMasterPrompt: string;
  aiWorkspaceMasterPromptId: string;

  feedbackSummary?: string;
  feedbackSummaryGeneratedAt?: string;
  feedbackSummaryStatus: keyof typeof draftingStatusConst;
  complianceCheckReport?: IComplianceReport;

  complianceCheck?: string;
  complianceCheckGeneratedAt?: string;
  complianceCheckStatus: keyof typeof draftingStatusConst;
  transcription?: {
    start: number;
    end: number;
    text: string;
    _id: string;
  }[];
  transcriptionGeneratedAt?: Date;
  transcriptionStatus?: keyof typeof draftingTranscriptionStatusConst;
  adSegements: {
    start: number;
    end: number;
  };
}

export interface IDraftingCommentsReplies {
  _id: string;
  repliedBy?: {
    firstName: string;
    lastName: string;
    profilePicUrl: string;
    _id: string;
  };
  repliedByModelName?: string;
  repliedByType?: string;
  text: string;
  createdAt: string; // ISO date
  clientReplyId?: string;
}

export interface IDraftingDrawingsPoint {
  x: number;
  y: number;
}

export interface IDraftingDrawings {
  points: IDraftingDrawingsPoint[];
  color: string;
}

export interface IDraftingComments {
  _id: string;
  text: string;
  drawings?: IDraftingDrawings[];
  timestamp: number; // in seconds, -1 = general comment
  createdAt?: string;
  commentedBy?: {
    firstName: string;
    id: string;
    lastName: string;
    profilePicUrl: string;
    _id: string;
  };
  commentedByModelName?: string;
  commentedByType?: string;
  isResolved?: boolean;
  replies: IDraftingCommentsReplies[];
  isGeneral?: boolean;
  mediaAssetId?: string;
  status?: string; //client side status
  clientCommentId?: string;
}

export interface IDraftingContents {
  _id: string;
  mediaAssets?: IDraftingMediaAssets[];
  comments?: IDraftingComments[];
  versionNumber?: number;

  createdBy?: ICreatedBy;
  createdByModelName?: string;
  createdByType?: string;

  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────
// ROOT: Drafting
// ─────────────────────────────────────────────

export interface IDrafting {
  _id: string;

  draftingContents?: IDraftingContents[];
  latestDraftingVersionId?: string;

  campaignInfluencerId?: string;
  campaignTaskId?: string;
  briefId?: string;
  brief?: IBrief;
  brand?: string;
  aiWorkspaceRequestId: string;
  brandId?: string;

  createdBy?: string;
  createdByModelName?: string;
  createdByType?: UserType;

  createdAt: string;
  updatedAt: string;
}

export interface IDraftingResponse {
  drafting: IDrafting;
  campaignInfluencer: BriefCampaignInf;
  campaignInfluencerTaskState: ICampaignDeliverableTask;
  campaignInfluencerDeliverableState: ICampaignDeliverable;
  briefDeliverableConfig: IGetBriefConfigResponse[];
}

export interface ICreateDraftComment {
  draftingId: string;
  draftingVersionId: string;
  comments: Pick<
    IDraftingComments,
    | "text"
    | "timestamp"
    | "drawings"
    | "isGeneral"
    | "mediaAssetId"
    | "clientCommentId"
  >;
}

export interface IDraftingCommentsReplies {
  draftingId: string;
  draftingVersionId: string;
  commentId: string;
  reply: string;
  status?: string;
  clientReplyId?: string;
}

export interface IVideoCompliance {
  draftingId: string;
  draftingVersionId: string;
  mediaAssetId: string;
}

export interface IResolveDraftingComment {
  draftingId: string;
  draftingVersionId: string;
  commentId: string;
  isResolved: boolean;
}

export interface IAdSegmentRequest extends IVideoCompliance {
  adSegments: {
    start: number;
    end: number;
  };
}

export interface IDeleteDraftingComment {
  draftingId: string;
  draftingVersionId: string;
  commentId: string;
}
